import { Grid, Typography } from "@mui/material";
import { FC, useEffect, useMemo, useState } from "react";
import BarChart from "../../components/charts/BarChart";
import SideInfo from "./SideInfo";
import {
  IClubsOverview,
  IClubsTableInfo,
} from "../../common/interfaces/club.interface";
import {
  getClubsOverview,
  getClubsTableInfo,
  getClubsChartInfo,
} from "../../api/platform/platform.clubs";
import AppCard from "../../components/cards/AppCard";
import { BasicTableWithTabs } from "../../components/tables/BasicTableWithTabs";
import { PeriodFilter, TableFilterClub } from "../../common/enums/common.enums";
import { clubTableArr } from "../../common/constants/chart.constants";
import { useTableClubContext } from "../../context/tabs/TableClubContext";
import { convertChartData } from "../../common/helpers/chart.helper";
import { convertStatisticData } from "../../common/helpers/statistics.helper";
import { IChart, IChartInfo } from "../../common/interfaces/chart.interface";
import { IStatistics } from "../../common/interfaces/statistic.interface";
import { useTabContext } from "../../context/tabs/TabContext";

const PlatformClubs: FC = () => {
  const [clubsOverview, setClubsOverview] = useState<IClubsOverview>();
  const [clubsTableInfo, setClubsTableInfo] = useState<IClubsTableInfo[]>([]);
  const [clubsChartInfo, setClubsChartInfo] = useState<IChart[]>();
  const [clubsStatisticInfo, setClubsStatisticInfo] = useState<IStatistics>();

  const [cardLoader, setCardLoader] = useState<boolean>(false);
  const [tableLoader, setTableLoader] = useState<boolean>(false);
  const [search, setSearch] = useState<string>("");
  const [tableTab, setTab] = useState<string>(TableFilterClub.BALANCE);
  const [chartTab, setChartTab] = useState<string>(PeriodFilter.DAILY);

  const changeTableTab = (tab: TableFilterClub): void => {
    getClubsTableInfoFromApi(tab);
    setTab(tab);
  };

  const changeChartTab = (tab: PeriodFilter): void => {
    setChartTab(tab);
  };

  useEffect(() => {
    void getClubsOverviewFromApi();
    void getClubsTableInfoFromApi(TableFilterClub.BALANCE);
    void getClubsChartInfoFromApi();
  }, []);

  const handleSearch = (value: string) => {
    setSearch(value);
  };

  const getClubsOverviewFromApi = async () => {
    try {
      setCardLoader(true);
      setClubsOverview(await getClubsOverview());
      setCardLoader(false);
    } catch (error) {
      console.log(error);
    }
  };

  const getClubsChartInfoFromApi = async () => {
    try {
      const response: IChartInfo = await getClubsChartInfo();
      const convertedChartData: IChart[] | undefined = convertChartData(
        response,
        "Clubs"
      );
      setClubsChartInfo(convertedChartData);
      const statisticData = convertStatisticData(response);
      setClubsStatisticInfo(statisticData);
      console.log(statisticData);
    } catch (error) {
      console.log(error);
    }
  };

  const getClubsTableInfoFromApi = async (criteria: TableFilterClub) => {
    try {
      setTableLoader(true);
      const response: IClubsTableInfo[] = await getClubsTableInfo(criteria);
      setClubsTableInfo(response);
      setTableLoader(false);
    } catch (error) {
      console.log(error);
    }
  };

  const activeClubs = useMemo(() => {
    return clubsOverview && clubsOverview.active;
  }, [clubsOverview]);

  const totalActiveClubs = useMemo(() => {
    return clubsOverview && clubsOverview.total;
  }, [clubsOverview]);

  const depositBased = useMemo(() => {
    return clubsOverview && clubsOverview.depositBased;
  }, [clubsOverview]);

  const roleBased = useMemo(() => {
    return clubsOverview && clubsOverview.roleBased;
  }, [clubsOverview]);

  const nftBased = useMemo(() => {
    return clubsOverview && clubsOverview.nftBased;
  }, [clubsOverview]);

  return (
    <div>
      <Grid
        container
        spacing={{ xs: 1, md: 2, sm: 2 }}
        columns={{ xs: 4, sm: 8, md: 12 }}
      >
        <Typography
          variant="h4"
          component="span"
          sx={{
            display: "flex",
            justifyContent: "start",
            padding: 1,
            width: "100%",
          }}
        >
          Clubs
        </Typography>
        <Grid item xs={12} sm={12} md={4.5}>
          <AppCard
            label={"Active clubs / Total clubs"}
            value={`${activeClubs} / ${totalActiveClubs}`}
            loader={cardLoader}
          />
        </Grid>
        <Grid item xs={12} sm={12} md={2.5}>
          <AppCard
            label={"Deposit based"}
            value={`${depositBased}`}
            loader={cardLoader}
          />
        </Grid>
        <Grid item xs={12} sm={12} md={2.5}>
          <AppCard
            label={"Role based"}
            value={`${roleBased}`}
            loader={cardLoader}
          />
        </Grid>
        <Grid item xs={12} sm={12} md={2.5}>
          <AppCard
            label={"NFT based"}
            value={`${nftBased}`}
            loader={cardLoader}
          />
        </Grid>
      </Grid>
      <Grid
        container
        spacing={{ xs: 1, md: 2, sm: 2 }}
        columns={{ xs: 4, sm: 8, md: 12 }}
        style={{ marginTop: 2 }}
      >
        <Grid item xs={12} sm={12} md={6}>
          <useTabContext.Provider
            value={{
              tab: chartTab,
              changeTab: changeChartTab,
            }}
          >
            <SideInfo statisticsData={clubsStatisticInfo} />
            <BarChart chartData={clubsChartInfo} />
          </useTabContext.Provider>
        </Grid>
        <Grid item xs={12} sm={12} md={6}>
          <useTableClubContext.Provider
            value={{
              tab: tableTab,
              changeTab: changeTableTab,
            }}
          >
            <Typography
              variant="h5"
              component="span"
              sx={{
                display: "flex",
                justifyContent: "start",
                padding: 1,
                width: "100%",
              }}
            >
              Top 5 clubs
            </Typography>
            <BasicTableWithTabs
              onSearch={handleSearch}
              loader={tableLoader}
              items={clubsTableInfo}
              tabs={clubTableArr}
            />
          </useTableClubContext.Provider>
        </Grid>
      </Grid>
    </div>
  );
};

export default PlatformClubs;
